import React, { useState, useMemo, useCallback } from "react";
import { SmartImg, webpOf } from "../lib/smartImg";
import { useApp } from "../context";
import { isAdminUser } from "../utils/adminCheck";

// ========================================================
// ISAIAH'S WORLD, PREMIUM AVATAR SYSTEM v6
// Roblox-inspired category browsing with Pixar art
// Category tabs → sub-filters → visual grid → live preview
// ========================================================

const CHARACTERS = [
  // Kids
  { id: "dark_bald_blue", name: "Fresh Start", image: "/assets/avatars/characters/dark_bald_blue.png", skin: "dark", hair: "bald", outfit: "tee", category: "kid" },
  { id: "dark_hightop_red", name: "High Top King", image: "/assets/avatars/characters/dark_hightop_red.png", skin: "dark", hair: "hightop", outfit: "hoodie", category: "kid" },
  { id: "brown_afro_green", name: "Afro Cool", image: "/assets/avatars/characters/brown_afro_green.png", skin: "brown", hair: "afro", outfit: "tee", category: "kid" },
  { id: "dark_dreads_varsity", name: "Varsity Dreads", image: "/assets/avatars/characters/dark_dreads_varsity.png", skin: "dark", hair: "dreads", outfit: "jacket", category: "kid" },
  { id: "brown_braids_pink", name: "Braids & Beads", image: "/assets/avatars/characters/brown_braids_pink.png", skin: "brown", hair: "braids", outfit: "hoodie", category: "kid" },
  { id: "light_straight_orange", name: "Sunny Days", image: "/assets/avatars/characters/light_straight_orange.png", skin: "light", hair: "straight", outfit: "tee", category: "kid" },
  { id: "light_straight_yellow", name: "Star Player", image: "/assets/avatars/characters/light_straight_yellow.png", skin: "light", hair: "long_straight", outfit: "tee", category: "kid" },
  { id: "tan_curly_navy", name: "Cool Curls", image: "/assets/avatars/characters/tan_curly_navy.png", skin: "tan", hair: "curly", outfit: "hoodie", category: "kid" },
  { id: "deep_fade_black", name: "Shadow Fade", image: "/assets/avatars/characters/deep_fade_black.png", skin: "deep", hair: "fade", outfit: "hoodie", category: "kid" },
  { id: "brown_mohawk_green", name: "Mohawk MVP", image: "/assets/avatars/characters/brown_mohawk_green.png", skin: "brown", hair: "mohawk", outfit: "jersey", category: "kid" },
  { id: "fair_wavy_denim", name: "Denim Vibes", image: "/assets/avatars/characters/fair_wavy_denim.png", skin: "fair", hair: "wavy", outfit: "jacket", category: "kid" },
  { id: "kid_indian_green", name: "Game On", image: "/assets/avatars/characters/kid_indian_green.png", skin: "tan", hair: "short", outfit: "tee", category: "kid" },
  { id: "kid_asian_red", name: "Dragon Spirit", image: "/assets/avatars/characters/kid_asian_red.png", skin: "light", hair: "spiky", outfit: "tee", category: "kid" },
  { id: "kid_arab_blue", name: "Explorer", image: "/assets/avatars/characters/kid_arab_blue.png", skin: "tan", hair: "curly", outfit: "hoodie", category: "kid" },
  // Adults
  { id: "adult_black_male_navy", name: "Strong Hero", image: "/assets/avatars/characters/adult_black_male_navy.png", skin: "dark", hair: "bald", outfit: "polo", category: "adult" },
  { id: "adult_black_female_pink", name: "Royal Spirit", image: "/assets/avatars/characters/adult_black_female_pink.png", skin: "brown", hair: "braids", outfit: "blouse", category: "adult" },
  { id: "adult_white_male_gray", name: "Cool Mentor", image: "/assets/avatars/characters/adult_white_male_gray.png", skin: "light", hair: "short", outfit: "henley", category: "adult" },
  { id: "adult_white_female_teal", name: "Kind Guide", image: "/assets/avatars/characters/adult_white_female_teal.png", skin: "light", hair: "wavy", outfit: "cardigan", category: "adult" },
  { id: "adult_hispanic_male_maroon", name: "Cool Champion", image: "/assets/avatars/characters/adult_hispanic_male_maroon.png", skin: "tan", hair: "short", outfit: "shirt", category: "adult" },
  { id: "adult_asian_female_lavender", name: "Gentle Soul", image: "/assets/avatars/characters/adult_asian_female_lavender.png", skin: "light", hair: "long_straight", outfit: "blouse", category: "adult" },
  // Premium Characters (unlockable via Avatar Shop)
  { id: "premium_braids_gold", name: "Golden Braids", image: "/assets/avatars/characters/premium_braids_gold.png", skin: "dark", hair: "braids", outfit: "jacket", category: "kid", premium: true, price: 100 },
  { id: "premium_lightning_gold", name: "Lightning Strike", image: "/assets/avatars/characters/premium_lightning_gold.png", skin: "dark", hair: "buzz_design", outfit: "tracksuit", category: "kid", premium: true, price: 150 },
  { id: "premium_space_explorer", name: "Space Explorer", image: "/assets/avatars/characters/premium_space_explorer.png", skin: "brown", hair: "curly", outfit: "spacesuit", category: "kid", premium: true, price: 200 },
  { id: "premium_superhero_cape", name: "Super Isaiah", image: "/assets/avatars/characters/premium_superhero_cape.png", skin: "dark", hair: "curly", outfit: "superhero", category: "kid", premium: true, price: 250 },
  { id: "premium_royal_prince", name: "Royal Prince", image: "/assets/avatars/characters/premium_royal_prince.png", skin: "dark", hair: "short", outfit: "royal", category: "kid", premium: true, price: 200 },
  { id: "premium_royal_princess", name: "Royal Princess", image: "/assets/avatars/characters/premium_royal_princess.png", skin: "brown", hair: "updo", outfit: "gown", category: "kid", premium: true, price: 200 },
  { id: "premium_ninja", name: "Shadow Ninja", image: "/assets/avatars/characters/premium_ninja.png", skin: "dark", hair: "hidden", outfit: "ninja", category: "kid", premium: true, price: 150 },
  { id: "premium_rockstar", name: "Rockstar", image: "/assets/avatars/characters/premium_rockstar.png", skin: "brown", hair: "spiky_blue", outfit: "leather", category: "kid", premium: true, price: 150 },
  // New diversity characters
  { id: "kid_korean_blue", name: "K-Star", image: "/assets/avatars/characters/kid_korean_blue.png", skin: "light", hair: "bowl_cut", outfit: "tee", category: "kid" },
  { id: "kid_african_orange", name: "Sunshine", image: "/assets/avatars/characters/kid_african_orange.png", skin: "deep", hair: "twa", outfit: "dress", category: "kid" },
  // Seasonal Exclusives
  { id: "seasonal_holiday", name: "Holiday Spirit", image: "/assets/avatars/characters/seasonal_holiday.png", skin: "brown", hair: "hidden", outfit: "christmas", category: "kid", premium: true, price: 100, seasonal: true },
  { id: "seasonal_halloween", name: "Lil Vampire", image: "/assets/avatars/characters/seasonal_halloween.png", skin: "light", hair: "slicked", outfit: "vampire", category: "kid", premium: true, price: 100, seasonal: true },
];

const BACKGROUNDS = [
  { id: "stars", name: "Stars", colors: ["#1e1b4b", "#581c87"] },
  { id: "ocean", name: "Ocean", colors: ["#2563eb", "#06b6d4"] },
  { id: "sunset", name: "Sunset", colors: ["#f97316", "#db2777"] },
  { id: "forest", name: "Forest", colors: ["#047857", "#22c55e"] },
  { id: "galaxy", name: "Galaxy", colors: ["#4c1d95", "#a21caf"] },
  { id: "arctic", name: "Arctic", colors: ["#bfdbfe", "#818cf8"] },
  { id: "lava", name: "Lava", colors: ["#b91c1c", "#f97316"] },
  { id: "neon", name: "Neon", colors: ["#ec4899", "#22d3ee"] },
];

const BODY_TYPES = [
  { id: "slim", name: "Slim", scaleX: 0.88, scaleY: 1.0 },
  { id: "average", name: "Average", scaleX: 1.0, scaleY: 1.0 },
  { id: "athletic", name: "Athletic", scaleX: 1.06, scaleY: 1.0 },
  { id: "stocky", name: "Stocky", scaleX: 1.14, scaleY: 0.97 },
  { id: "heavy", name: "Big Build", scaleX: 1.22, scaleY: 0.95 },
];

const HEIGHT_OPTIONS = [
  { id: "small", name: "Young Kid", scale: 0.82, label: "Pre-K - 2nd" },
  { id: "medium", name: "Kid", scale: 0.91, label: "3rd - 5th" },
  { id: "tall", name: "Big Kid", scale: 1.0, label: "6th - 8th" },
  { id: "adult", name: "Adult", scale: 1.0, label: "Parent" },
];

// Filter definitions for the category-based UI
const SKIN_FILTERS = [
  { id: "all", name: "All", color: null },
  { id: "deep", name: "Deep", color: "#3B2317" },
  { id: "dark", name: "Dark", color: "#5A3826" },
  { id: "brown", name: "Brown", color: "#8D5A3C" },
  { id: "tan", name: "Tan", color: "#C07D54" },
  { id: "light", name: "Light", color: "#E8B98F" },
  { id: "fair", name: "Fair", color: "#FDE7D6" },
];

const HAIR_FILTERS = [
  { id: "all", name: "All" }, { id: "bald", name: "Bald" }, { id: "fade", name: "Fade" },
  { id: "hightop", name: "High-Top" }, { id: "afro", name: "Afro" }, { id: "dreads", name: "Dreads" },
  { id: "braids", name: "Braids" }, { id: "curly", name: "Curly" }, { id: "straight", name: "Straight" },
  { id: "long_straight", name: "Long Straight" }, { id: "wavy", name: "Wavy" }, { id: "mohawk", name: "Mohawk" },
  { id: "spiky", name: "Spiky" }, { id: "short", name: "Short" },
];

const OUTFIT_FILTERS = [
  { id: "all", name: "All" }, { id: "tee", name: "T-Shirts" }, { id: "hoodie", name: "Hoodies" },
  { id: "jacket", name: "Jackets" }, { id: "jersey", name: "Jerseys" }, { id: "polo", name: "Polos" },
  { id: "blouse", name: "Blouses" }, { id: "shirt", name: "Shirts" },
];

const DEFAULT_AVATAR_CONFIG = {
  version: 6,
  character_id: "dark_bald_blue",
  background: "stars",
  body_type: "average",
  height: "medium",
};

const migrateConfig = (config) => {
  if (!config) return DEFAULT_AVATAR_CONFIG;
  if (config.version >= 5 || config.version === 3) {
    return { ...DEFAULT_AVATAR_CONFIG, ...config, version: 6 };
  }
  const hair = config.hair_style || "";
  let bestId = "dark_bald_blue";
  if (hair.includes("afro")) bestId = "brown_afro_green";
  else if (hair.includes("dread") || hair.includes("loc")) bestId = "dark_dreads_varsity";
  else if (hair.includes("braid")) bestId = "brown_braids_pink";
  else if (hair.includes("high_top")) bestId = "dark_hightop_red";
  else if (hair.includes("fade")) bestId = "deep_fade_black";
  else if (hair.includes("mohawk")) bestId = "brown_mohawk_green";
  return { ...DEFAULT_AVATAR_CONFIG, character_id: bestId, background: config.background || "stars" };
};


// ============== AVATAR RENDERER ==============

const AvatarRenderer = ({ config, size = 280, animate = false, equipped = null }) => {
  const c = useMemo(() => migrateConfig(config), [config]);
  const char = CHARACTERS.find(ch => ch.id === c.character_id) || CHARACTERS[0];
  const bg = BACKGROUNDS.find(b => b.id === c.background) || BACKGROUNDS[0];
  const bt = BODY_TYPES.find(b => b.id === c.body_type) || BODY_TYPES[1];
  const ht = HEIGHT_OPTIONS.find(h => h.id === c.height) || HEIGHT_OPTIONS[1];

  // Support custom AI-generated avatars
  const imageUrl = c.custom_image || char.image;

  // Equipped cosmetics from marketplace (optional, passed from parent)
  // equipped shape: { hat: {icon,...}, accessory: {...}, shoes: {...}, pet: {...}, badge: {...} }
  const eq = equipped || {};

  return (
    <div data-testid="avatar-preview" style={{
      position: "relative",
      width: size, height: size * 1.2, borderRadius: 16, overflow: "hidden",
      background: `linear-gradient(135deg, ${bg.colors[0]}, ${bg.colors[1]})`,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      animation: animate ? "avatarFloat 4s ease-in-out infinite" : undefined,
    }}>
      <SmartImg src={imageUrl} alt={char.name} draggable={false} style={{
        width: "92%", height: "auto", objectFit: "contain", objectPosition: "bottom center",
        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
        transform: `scaleX(${bt.scaleX}) scaleY(${bt.scaleY * ht.scale})`,
        transformOrigin: "bottom center", transition: "transform 0.3s ease",
      }} />

      {/* Equipped cosmetics overlay */}
      {eq.hat && (
        <div data-testid="equipped-hat" style={{
          position: "absolute", top: `${size * 0.04}px`, left: "50%", transform: "translateX(-50%)",
          fontSize: `${size * 0.28}px`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
          animation: "avatarCosmeticPulse 2.5s ease-in-out infinite",
        }}>{eq.hat.icon}</div>
      )}
      {eq.accessory && (
        <div data-testid="equipped-accessory" style={{
          position: "absolute", top: `${size * 0.42}px`, right: `${size * 0.06}px`,
          fontSize: `${size * 0.22}px`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
        }}>{eq.accessory.icon}</div>
      )}
      {eq.shoes && (
        <div data-testid="equipped-shoes" style={{
          position: "absolute", bottom: `${size * 0.02}px`, left: "50%", transform: "translateX(-50%)",
          fontSize: `${size * 0.16}px`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
        }}>{eq.shoes.icon}</div>
      )}
      {eq.pet && (
        <div data-testid="equipped-pet" style={{
          position: "absolute", bottom: `${size * 0.06}px`, right: `${size * 0.04}px`,
          fontSize: `${size * 0.18}px`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
          animation: "avatarPetBounce 1.8s ease-in-out infinite",
        }}>{eq.pet.icon}</div>
      )}
      {eq.badge && (
        <div data-testid="equipped-badge" style={{
          position: "absolute", top: `${size * 0.08}px`, right: `${size * 0.06}px`,
          fontSize: `${size * 0.14}px`,
          padding: "4px", borderRadius: "50%",
          background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.2)",
        }}>{eq.badge.icon}</div>
      )}

      <style>{`
        @keyframes avatarFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
        @keyframes avatarCosmeticPulse{0%,100%{transform:translateX(-50%) scale(1)}50%{transform:translateX(-50%) scale(1.05)}}
        @keyframes avatarPetBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
      `}</style>
    </div>
  );
};


// ============== AVATAR BUILDER (Roblox-style categories) ==============

const AvatarBuilder = ({ initialConfig, onSave, onCancel, compact = false, equipped = null }) => {
  const { user } = useApp();
  const initial = useMemo(() => migrateConfig(initialConfig), [initialConfig]);
  const [selectedChar, setSelectedChar] = useState(initial.character_id);
  const [selectedBg, setSelectedBg] = useState(initial.background);
  const [bodyType, setBodyType] = useState(initial.body_type || "average");
  const [height, setHeight] = useState(initial.height || "medium");

  // Category navigation
  const [mainTab, setMainTab] = useState("style");
  const [skinFilter, setSkinFilter] = useState("all");
  const [hairFilter, setHairFilter] = useState("all");
  const [outfitFilter, setOutfitFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all"); // "kid" or "adult"

  const selectedCharData = CHARACTERS.find(c => c.id === selectedChar);
  const isAdult = selectedCharData?.category === "adult";

  const currentConfig = useMemo(() => ({
    version: 6, character_id: selectedChar, background: selectedBg,
    body_type: bodyType, height: isAdult ? "adult" : height,
  }), [selectedChar, selectedBg, bodyType, height, isAdult]);

  // Filter characters based on active filters
  const filteredChars = useMemo(() => {
    let chars = CHARACTERS;
    if (ageFilter !== "all") chars = chars.filter(c => c.category === ageFilter);
    if (skinFilter !== "all") chars = chars.filter(c => c.skin === skinFilter);
    if (hairFilter !== "all") chars = chars.filter(c => c.hair === hairFilter);
    if (outfitFilter !== "all") chars = chars.filter(c => c.outfit === outfitFilter);
    return chars;
  }, [ageFilter, skinFilter, hairFilter, outfitFilter]);

  const handleSave = useCallback(() => { if (onSave) onSave(currentConfig); }, [onSave, currentConfig]);

  const mainTabs = [
    { id: "style", label: "All Styles" },
    { id: "skin", label: "Skin" },
    { id: "hair", label: "Hair" },
    { id: "outfit", label: "Outfit" },
    { id: "body", label: "Body" },
    { id: "bg", label: "BG" },
  ];

  // Reset sub-filters when switching main tabs
  const switchMainTab = (tabId) => {
    setMainTab(tabId);
    if (tabId === "style") { setSkinFilter("all"); setHairFilter("all"); setOutfitFilter("all"); }
  };

  const charCard = (char) => (
    <button key={char.id} onClick={() => setSelectedChar(char.id)} data-testid={`avatar-char-${char.id}`}
      className={`relative rounded-xl overflow-hidden border-[3px] transition-all duration-200 aspect-[3/4]
        ${selectedChar === char.id ? "border-amber-400 shadow-[0_0_16px_rgba(251,191,36,0.35)] scale-[1.03]" : "border-white/8 hover:border-white/20 hover:scale-[1.01]"}`}
      style={{ background: "linear-gradient(135deg, #1e1b4b, #374151)" }}>
      <SmartImg src={char.image} alt={char.name} className="w-full h-full object-contain object-bottom" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))" }} draggable={false} loading="lazy" />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1.5">
        <span className={`text-[9px] font-bold block text-center ${selectedChar === char.id ? "text-amber-300" : "text-white/70"}`}>{char.name}</span>
      </div>
      {selectedChar === char.id && <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3" strokeLinecap="round"><path d="M5 12l5 5L20 7"/></svg></div>}
    </button>
  );

  const subTabBtn = (label, active, onClick, testId) => (
    <button onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap
        ${active ? "bg-amber-400/15 text-amber-300 border border-amber-400/30" : "text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent"}`}>
      {label}
    </button>
  );

  return (
    <div className={`flex ${compact ? "flex-col" : "flex-col lg:flex-row"} gap-6`} data-testid="avatar-builder">
      {/* Left: Live Preview */}
      <div className="flex flex-col items-center gap-4 shrink-0">
        <div className="relative">
          <div className="absolute -inset-1.5 rounded-[20px] bg-gradient-to-br from-amber-500/20 via-transparent to-purple-500/20 blur-sm opacity-60" />
          <div className="relative"><AvatarRenderer config={currentConfig} size={compact ? 200 : 280} animate equipped={equipped} /></div>
        </div>
        {onSave && (
          <div className="flex gap-2 w-full max-w-[280px]">
            <button onClick={handleSave} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-sm hover:from-amber-400 hover:to-yellow-400 transition-all shadow-lg shadow-amber-500/25 active:scale-[0.98]" data-testid="avatar-save-btn">Save Avatar</button>
            {onCancel && <button onClick={onCancel} className="px-5 py-3 rounded-xl border border-white/10 text-white/50 text-sm hover:bg-white/5 transition-all" data-testid="avatar-cancel-btn">Cancel</button>}
          </div>
        )}
        {isAdminUser(user) && (
        <a href="/avatar/create" className="mt-2 w-full max-w-[280px] py-2 rounded-xl border border-dashed border-amber-500/20 text-amber-300/40 text-[11px] font-medium text-center hover:border-amber-500/40 hover:text-amber-300/60 hover:bg-amber-500/5 transition-all flex items-center justify-center gap-1.5" data-testid="ai-creator-link">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/></svg>
          AI Avatar Creator (Premium)
        </a>
        )}
      </div>

      {/* Right: Category Browser */}
      <div className="flex-1 min-w-0">
        {/* Main Category Tabs */}
        <div className="flex gap-0.5 mb-4 p-1 bg-white/[0.04] rounded-xl border border-white/[0.06] overflow-x-auto">
          {mainTabs.map(t => (
            <button key={t.id} onClick={() => switchMainTab(t.id)} data-testid={`avatar-tab-${t.id}`}
              className={`flex-shrink-0 py-2 px-3 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-all
                ${mainTab === t.id ? "bg-white/10 text-white shadow-sm" : "text-white/30 hover:text-white/50"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[440px] overflow-y-auto pr-1 scroll-smooth" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>

          {/* ALL STYLES */}
          {mainTab === "style" && (
            <>
              {/* Age filter */}
              <div className="flex gap-2 mb-3 flex-wrap">
                {subTabBtn("All", ageFilter === "all", () => setAgeFilter("all"), "filter-age-all")}
                {subTabBtn("Kids", ageFilter === "kid", () => setAgeFilter("kid"), "filter-age-kid")}
                {subTabBtn("Adults", ageFilter === "adult", () => setAgeFilter("adult"), "filter-age-adult")}
                {subTabBtn("Premium", ageFilter === "premium", () => setAgeFilter("premium"), "filter-age-premium")}
                {subTabBtn("Seasonal", ageFilter === "seasonal", () => setAgeFilter("seasonal"), "filter-age-seasonal")}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {(ageFilter === "all" ? CHARACTERS : ageFilter === "premium" ? CHARACTERS.filter(c => c.premium && !c.seasonal) : ageFilter === "seasonal" ? CHARACTERS.filter(c => c.seasonal) : CHARACTERS.filter(c => c.category === ageFilter && !c.premium)).map(charCard)}
              </div>
            </>
          )}

          {/* SKIN TONE */}
          {mainTab === "skin" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                {SKIN_FILTERS.map(sf => (
                  <button key={sf.id} onClick={() => setSkinFilter(sf.id)} data-testid={`filter-skin-${sf.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all
                      ${skinFilter === sf.id ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-white/8 text-white/40 hover:border-white/20"}`}>
                    {sf.color && <div className="w-4 h-4 rounded-full border border-white/10" style={{ background: sf.color }} />}
                    <span className="text-[11px] font-medium">{sf.name}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filteredChars.map(charCard)}
              </div>
              {filteredChars.length === 0 && <p className="text-white/25 text-sm text-center py-8">No matches, try a different filter</p>}
            </>
          )}

          {/* HAIR */}
          {mainTab === "hair" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                {HAIR_FILTERS.map(hf => (
                  subTabBtn(hf.name, hairFilter === hf.id, () => setHairFilter(hf.id), `filter-hair-${hf.id}`)
                ))}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filteredChars.map(charCard)}
              </div>
              {filteredChars.length === 0 && <p className="text-white/25 text-sm text-center py-8">No matches, try a different filter</p>}
            </>
          )}

          {/* OUTFIT */}
          {mainTab === "outfit" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                {OUTFIT_FILTERS.map(of => (
                  subTabBtn(of.name, outfitFilter === of.id, () => setOutfitFilter(of.id), `filter-outfit-${of.id}`)
                ))}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filteredChars.map(charCard)}
              </div>
              {filteredChars.length === 0 && <p className="text-white/25 text-sm text-center py-8">No matches, try a different filter</p>}
            </>
          )}

          {/* BODY */}
          {mainTab === "body" && (
            <>
              <h3 className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-3">Body Type</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {BODY_TYPES.map(bt => (
                  <button key={bt.id} onClick={() => setBodyType(bt.id)} data-testid={`avatar-body-${bt.id}`}
                    className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border-2 transition-all min-w-[72px]
                      ${bodyType === bt.id ? "border-amber-400 bg-amber-400/10" : "border-white/8 bg-white/[0.03] hover:border-white/20"}`}>
                    <svg viewBox="0 0 32 40" width="28" height="35">
                      <circle cx="16" cy="7" r="5" fill={bodyType === bt.id ? "#fbbf24" : "#666"} opacity="0.7" />
                      <rect x={16 - 5 * bt.scaleX} y="13" width={10 * bt.scaleX} height={14 * bt.scaleY} rx="3" fill={bodyType === bt.id ? "#fbbf24" : "#666"} opacity="0.5" />
                      <rect x={16 - 3 * bt.scaleX} y="26" width={2.5 * bt.scaleX} height="10" rx="1.5" fill={bodyType === bt.id ? "#fbbf24" : "#666"} opacity="0.4" />
                      <rect x={16 + 0.5 * bt.scaleX} y="26" width={2.5 * bt.scaleX} height="10" rx="1.5" fill={bodyType === bt.id ? "#fbbf24" : "#666"} opacity="0.4" />
                    </svg>
                    <span className={`text-[10px] font-medium ${bodyType === bt.id ? "text-amber-300" : "text-white/45"}`}>{bt.name}</span>
                  </button>
                ))}
              </div>
              {!isAdult && (
                <>
                  <h3 className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-3">Height / Age</h3>
                  <div className="flex flex-wrap gap-2">
                    {HEIGHT_OPTIONS.filter(h => h.id !== "adult").map(ht => (
                      <button key={ht.id} onClick={() => setHeight(ht.id)} data-testid={`avatar-height-${ht.id}`}
                        className={`flex flex-col items-center gap-1 px-4 py-3 rounded-xl border-2 transition-all min-w-[80px]
                          ${height === ht.id ? "border-amber-400 bg-amber-400/10" : "border-white/8 bg-white/[0.03] hover:border-white/20"}`}>
                        <div className="flex items-end gap-0.5 h-8"><div className={`w-2 rounded-t ${height === ht.id ? "bg-amber-400" : "bg-white/20"}`} style={{ height: `${ht.scale * 32}px` }} /></div>
                        <span className={`text-[10px] font-medium ${height === ht.id ? "text-amber-300" : "text-white/45"}`}>{ht.name}</span>
                        <span className={`text-[8px] ${height === ht.id ? "text-amber-300/60" : "text-white/25"}`}>{ht.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* BACKGROUND */}
          {mainTab === "bg" && (
            <>
              <h3 className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-3">Background</h3>
              <div className="grid grid-cols-4 gap-3">
                {BACKGROUNDS.map(bg => (
                  <button key={bg.id} onClick={() => setSelectedBg(bg.id)} data-testid={`avatar-bg-${bg.id}`}
                    className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all
                      ${selectedBg === bg.id ? "border-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.3)]" : "border-white/8 hover:border-white/20"}`}>
                    <div className="w-full aspect-square rounded-lg" style={{ background: `linear-gradient(135deg, ${bg.colors[0]}, ${bg.colors[1]})` }} />
                    <span className={`text-[9px] ${selectedBg === bg.id ? "text-amber-300" : "text-white/35"}`}>{bg.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ============== EXPORTS ==============
const SKIN_TONES = [];
const HAIR_STYLES = [];
const HAIR_COLORS = [];
const EYE_STYLES = [];
const EYE_COLORS = [];
const EXPRESSIONS = [];
const TOPS = [];
const BOTTOMS = [];
const SHOES = [];
const ACCESSORIES_HEAD = [];
const ACCESSORIES_FACE = [];
const ALL_ITEM_CATEGORIES = ["character"];
const useAvatarInventory = (userId) => {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchInventory = useCallback(async () => {
    if (!userId) return;
    try { const API = process.env.REACT_APP_BACKEND_URL; const res = await fetch(`${API}/api/profile/avatar/inventory/${userId}`); setInventory(await res.json()); }
    catch { setInventory({ user_id: userId, items: [] }); }
    finally { setLoading(false); }
  }, [userId]);
  const isOwned = useCallback((itemId) => !inventory?.items || inventory.items.some(i => i.item_id === itemId), [inventory]);
  const getOwnedByCategory = useCallback((cat) => inventory?.items?.filter(i => i.category === cat) || [], [inventory]);
  return { inventory, loading, fetchInventory, isOwned, getOwnedByCategory };
};

export { AvatarRenderer, AvatarBuilder, SKIN_TONES, HAIR_STYLES, HAIR_COLORS, EYE_STYLES, EYE_COLORS, EXPRESSIONS, TOPS, BOTTOMS, SHOES, ACCESSORIES_HEAD, BACKGROUNDS };
export { DEFAULT_AVATAR_CONFIG, BODY_TYPES, ACCESSORIES_FACE, useAvatarInventory, ALL_ITEM_CATEGORIES, CHARACTERS, HEIGHT_OPTIONS };
export default AvatarBuilder;
